import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Modal, Row, Select, Spin, message } from "antd";
import { LoadingOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { getMenuList, getUserRoleId, getBillingEntityList } from "redux/selectors";

const FINANCE_SETTINGS_URL = "settings/finance/finance-settings";
const CURRENCY_LIST_URL = "settings/finance/currency-list";

const FinanceStyle = styled.div`
  .finance_header_content,
  .finance_header_action {
    width: max-content;
  }
  .finance_header_action .ant-btn {
    margin-left: 8px;
  }
  .finance_footer_action {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
  .finance_footer_action .ant-btn {
    margin-left: 8px;
  }
  .ant-form-item {
    margin-bottom: 12px;
  }
  .finance_section_title {
    font-size: 16px;
    font-weight: bold;
    padding-bottom: 8px;
    margin-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
  }
  .finance_section + .finance_section {
    margin-top: 16px;
  }
  .finance_account_row {
    display: flex;
    align-items: center;
  }
  .finance_account_group {
    display: flex;
    flex: 1;
  }
  .finance_account_remove {
    margin-left: 8px;
    font-size: 18px;
    color: #FF5353;
    cursor: pointer;
  }
  .finance_account_group .finance_currency_select {
    width: 110px;
    flex: none;
  }
  .finance_account_group > .ant-input {
    flex: 1;
  }
  .ant-input[disabled],
  .ant-select-disabled.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    color: rgba(0, 0, 0, 0.85);
    background: #fafafa;
    cursor: default;
  }
`;

const UNSAFE_PATTERN = /<[^>]*>|[{}\[\]\\%]|javascript:/i;

const safeTextRule = {
  validator: (_, value) =>
    value && UNSAFE_PATTERN.test(value)
      ? Promise.reject("Invalid characters or HTML tags are not allowed.")
      : Promise.resolve(),
};

// Form field name -> API payload key for the invoice header block.
// span: grid width out of 24 (defaults to half width).
const TELEPHONE_PATTERN = /^[\d\s+()-]{3,20}$/;

const COMPANY_FIELDS = [
  {
    name: "companyName",
    payload: "companyname",
    label: "Company Name",
    required: true,
    span: 24,
    placeholder: "e.g. BDO ZAMBIA LIMITED",
    rules: [
      {
        pattern: /^[a-zA-Z0-9 .,'()&/-]{1,150}$/,
        message: "Only letters, numbers, spaces and . , ' ( ) & - / are allowed (max 150 characters).",
      },
    ],
  },
  {
    name: "address",
    payload: "address",
    required: true,
    label: "Address",
    type: "textarea",
    span: 24,
    placeholder: "e.g. Frost Building, The Gallery Office Park, Lagos Road, Rhodes Park",
    rules: [
      {
        pattern: /^[a-zA-Z0-9 \t\r\n.,'()_@:+=\-<>/]{1,1000}$/,
        message: "Invalid characters in address (max 1000 characters).",
      },
    ],
  },
  {
    name: "poBox",
    payload: "pobox",
    label: "P.O. Box",
    required: true,
    placeholder: "e.g. 35139",
    rules: [
      {
        pattern: /^[a-zA-Z0-9 .,'/-]{1,150}$/,
        message: "Only letters, numbers, spaces and . , ' - / are allowed (max 150 characters).",
      },
    ],
  },
  {
    name: "city",
    payload: "city",
    label: "City & Country",
    required: true,
    placeholder: "e.g. Lusaka",
    rules: [
      {
        pattern: /^[a-zA-Z0-9\s._,'/-]{1,100}$/,
        message: "Only letters, numbers, spaces and . _ , ' - / are allowed (max 100 characters).",
      },
    ],
  },
  {
    name: "telephone",
    payload: "telephone",
    required: true,
    label: "Telephone",
    type: "tags",
    placeholder: "Type a number and press Enter",
    rules: [
      {
        validator: (_, value) =>
          (value || []).every((number) => TELEPHONE_PATTERN.test(number))
            ? Promise.resolve()
            : Promise.reject("Each telephone may only contain digits, spaces and + ( ) - (3-20 characters)."),
      },
    ],
  },
  {
    name: "email",
    payload: "email",
    required: true,
    label: "Email",
    placeholder: "e.g. finance@bdo.co.zm",
    rules: [{ type: "email", message: "Please enter a valid email." }],
  },
  {
    name: "tpin",
    payload: "tpin",
    required: true,
    label: "TPIN",
    placeholder: "e.g. 1008839570",
    rules: [
      {
        // Digit count varies by country's TPIN format, so only the
        // character set is restricted here, not the length.
        pattern: /^\d{1,50}$/,
        message: "Only numbers are allowed.",
      },
    ],
  },
];

// Telephone is a list; tolerate a single "254375 / 250631" style string too.
const toTelephoneList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return String(value)
    .split(/[\/,]/)
    .map((number) => number.trim())
    .filter(Boolean);
};

const ORG_NAME_PATTERN = /^[a-zA-Z0-9 .,'()&/-]{1,150}$/;
const ORG_NAME_MESSAGE = "Only letters, numbers, spaces and . , ' ( ) & - / are allowed (max 150 characters).";

const BANK_FIELDS = [
  {
    name: "bankName",
    payload: "bankname",
    label: "Bank Name",
    required: true,
    placeholder: "e.g. Stanbic Bank Zambia",
    rules: [{ pattern: ORG_NAME_PATTERN, message: ORG_NAME_MESSAGE }],
  },
  {
    name: "bankBranch",
    payload: "bankbranch",
    label: "Bank Branch",
    required: true,
    placeholder: "e.g. Lusaka Main Branch",
    rules: [{ pattern: ORG_NAME_PATTERN, message: ORG_NAME_MESSAGE }],
  },
  {
    name: "swiftCode",
    payload: "swiftcode",
    required: true,
    label: "Swift Code",
    placeholder: "e.g. SBICZMLX",
    rules: [
      {
        pattern: /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/,
        message: "Swift Code must be 8 or 11 characters (e.g. SBICZMLX).",
      },
    ],
  },
  // Repeatable currency + account number pairs (Form.List), each row stored
  // as { currency: currency_config.currencycode, accountNumber }.
  {
    name: "bankAccounts",
    payload: "bankaccounts",
    required: true,
    label: "Account Number",
    type: "bankAccounts",
    span: 24,
  },
];

const ACCOUNT_NUMBER_PATTERN = /^[A-Za-z0-9\s-]{4,34}$/;

const EMPTY_BANK_ACCOUNT = { currency: undefined, accountNumber: "" };

const ALL_FIELDS = [...COMPANY_FIELDS, ...BANK_FIELDS];

// Maps the backend's payload key (e.g. "companyname") back to the form field
// name (e.g. "companyName"), so a server-side validation error can be shown
// next to the field it belongs to instead of only in a toast.
const FIELD_NAME_BY_PAYLOAD = ALL_FIELDS.reduce((acc, field) => {
  acc[field.payload] = field.name;
  return acc;
}, {});

const findMenuPermission = (menuList, key) => {
  for (const menu of menuList || []) {
    if (menu?.key === key) return menu?.permission;
    const sub = menu?.subMenu?.find((item) => item?.key === key);
    if (sub) return sub?.permission;
  }
  return undefined;
};

function FinanceSettings() {
  const { entityId } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currencyList, setCurrencyList] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);
  const billingEntityList = useSelector(getBillingEntityList);
  const entityName = (billingEntityList || []).find(
    (entity) => String(entity.id) === String(entityId)
  )?.name;

  const Menulist = menuList?.[userRoleId] || Object.values(menuList || {})[0];
  const canEdit = !!findMenuPermission(Menulist, "finance-settings")?.edit;

  // Stores the currency_config.currencycode (e.g. "ZMW").
  // Closed select shows just the code; the dropdown lists "ZMW - Zambian Kwacha".
  const currencyOptions = currencyList.map((item) => ({
    label: `${item.currencyCode} - ${item.currencyLabel}`,
    code: item.currencyCode,
    value: item.currencyCode,
  }));

  const getRequiredRule = (field) => {
    if (field.type === "tags") {
      return { required: true, type: "array", min: 1, message: `Please enter at least one ${field.label}.` };
    }
    return { required: true, whitespace: true, message: `Please enter ${field.label}.` };
  };

  const getFieldRules = (field) => [
    ...(field.required ? [getRequiredRule(field)] : []),
    ...(field.type === "tags" ? [] : [safeTextRule]),
    ...(field.rules || []),
  ];

  const renderBankAccounts = (field) => (
    <Form.List
      name={field.name}
      validateTrigger={["onChange", "onBlur"]}
      rules={[
        {
          validator: async (_, accounts) => {
            if (!accounts || accounts.length === 0) {
              throw new Error("Please add at least one account.");
            }
            const keys = accounts
              .filter((account) => account?.currency && account?.accountNumber)
              .map((account) => `${account.currency}|${account.accountNumber.trim().toUpperCase()}`);
            if (new Set(keys).size !== keys.length) {
              throw new Error("The same account has been added more than once.");
            }
          },
        },
      ]}
    >
      {(rows, { add, remove }, { errors }) => (
        <Row gutter={16}>
          {rows.map(({ key, name }, index) => (
            <Col xs={24} sm={12} key={key}>
              <Form.Item
                label={rows.length > 1 ? `${field.label} ${index + 1}` : field.label}
                required
              >
                <div className="finance_account_row">
                  <Input.Group compact className="finance_account_group">
                    <Form.Item
                      name={[name, "currency"]}
                      noStyle
                      rules={[{ required: true, message: "Please select Currency." }]}
                      validateTrigger={["onChange", "onBlur"]}
                    >
                      <Select
                        className="finance_currency_select"
                        placeholder={isEditing ? "Currency" : "-"}
                        options={currencyOptions}
                        optionLabelProp="code"
                        optionFilterProp="label"
                        dropdownMatchSelectWidth={false}
                        disabled={!isEditing}
                        showSearch
                      />
                    </Form.Item>
                    <Form.Item
                      name={[name, "accountNumber"]}
                      noStyle
                      rules={[
                        { required: true, whitespace: true, message: `Please enter ${field.label}.` },
                        safeTextRule,
                        {
                          pattern: ACCOUNT_NUMBER_PATTERN,
                          message: "Only letters, numbers, spaces and - are allowed (4-34 characters).",
                        },
                      ]}
                      validateTrigger={["onChange", "onBlur"]}
                    >
                      <Input
                        placeholder={isEditing ? "e.g. 9130001234567" : "-"}
                        disabled={!isEditing}
                        maxLength={34}
                      />
                    </Form.Item>
                  </Input.Group>
                  {isEditing && rows.length > 1 && (
                    <MinusCircleOutlined
                      className="finance_account_remove"
                      title="Remove account"
                      onClick={() => remove(name)}
                    />
                  )}
                </div>
              </Form.Item>
            </Col>
          ))}
          {isEditing && (
            <Col xs={24} sm={12}>
              <Form.Item label=" " colon={false}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ ...EMPTY_BANK_ACCOUNT })}
                  block
                >
                  Add Account
                </Button>
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.ErrorList errors={errors} />
          </Col>
        </Row>
      )}
    </Form.List>
  );

  const renderInput = (field) => {
    const placeholder = isEditing ? field.placeholder : "-";
    if (field.type === "textarea") {
      return (
        <Input.TextArea
          placeholder={placeholder}
          disabled={!isEditing}
          autoSize={{ minRows: 3, maxRows: 6 }}
          maxLength={500}
        />
      );
    }
    if (field.type === "tags") {
      return (
        <Select
          mode="tags"
          placeholder={placeholder}
          disabled={!isEditing}
          tokenSeparators={[",", "/"]}
          open={false}
          suffixIcon={null}
        />
      );
    }
    return <Input placeholder={placeholder} disabled={!isEditing} maxLength={150} />;
  };

  const renderFields = (fields) =>
    fields
      .map((field) => (
        <Col xs={24} sm={field.span || 12} key={field.name}>
          {field.type === "bankAccounts" ? (
            renderBankAccounts(field)) : (
            <Form.Item
              name={field.name}
              label={field.label}
              rules={getFieldRules(field)}
              validateTrigger={["onChange", "onBlur"]}
            >
              {renderInput(field)}
            </Form.Item>
          )}
        </Col>
      ));

  const getCurrencyList = async () => {
    try {
      const { code, data } = await CALL_API(CURRENCY_LIST_URL, "get");
      setCurrencyList(code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("[getCurrencyList], Error-->", error);
    }
  };

  const getFinanceSettings = async () => {
    setLoading(true);
    setLoadError("");
    try {
      // CALL_API returns the inner response on success, otherwise
      // { status, response: { message } } or the raw error body.
      const url = entityId
        ? `${FINANCE_SETTINGS_URL}?entityId=${entityId}`
        : FINANCE_SETTINGS_URL;
      const result = await CALL_API(url, "get");
      if (result?.code === STATUS_CODE.SUCCESS && Array.isArray(result?.data)) {
        setDetails(result.data[0] || null);
      } else {
        setDetails(null);
        setLoadError(
          result?.response?.message ||
          result?.message ||
          "Unable to load finance settings."
        );
      }
    } catch (error) {
      console.log("[getFinanceSettings], Error-->", error);
      setDetails(null);
      setLoadError("Unable to load finance settings.");
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    getFinanceSettings();
    getCurrencyList();
  }, [entityId]);

  const resetToDetails = () => {
    form.resetFields();
    form.setFieldsValue({
      ...(details || {}),
      telephone: toTelephoneList(details?.telephone),
      bankAccounts: details?.bankAccounts?.length
        ? details.bankAccounts
        : [{ ...EMPTY_BANK_ACCOUNT }],
    });
  };

  useEffect(() => {
    resetToDetails();
  }, [details]);

  const handleEdit = () => {
    resetToDetails();
    setIsEditing(true);
  };

  const handleCancel = () => {
    const reset = () => {
      resetToDetails();
      setIsEditing(false);
    };
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes. Are you sure you want to cancel?",
        okText: "Yes, discard",
        cancelText: "No, keep editing",
        onOk: reset,
      });
    } else {
      reset();
    }
  };

  const onFinish = async (values) => {
    const payload = ALL_FIELDS.reduce(
      (acc, field) => {
        acc[field.payload] =
          field.type === "bankAccounts"
            ? (values[field.name] || []).map((account) => ({
              currency: account.currency,
              accountnumber: account.accountNumber?.trim(),
            }))
            : values[field.name]?.trim?.() ?? values[field.name] ?? "";
        return acc;
      },
      {}
    );

    setSaving(true);
    // Upserts: the backend creates the row for this entity if none exists yet,
    // otherwise updates the existing one.
    const result = await CALL_API(
      `${FINANCE_SETTINGS_URL}/${entityId}`,
      "patch",
      payload
    );
    setSaving(false);

    if (result?.code === STATUS_CODE.SUCCESS) {
      message.success("Finance settings have been saved successfully");
      setIsEditing(false);
      getFinanceSettings();
    } else {
      const errors = result?.response?.errors;
      const fieldErrors = [];
      const generalMessages = [];

      (Array.isArray(errors) ? errors : []).forEach((error) => {
        const text = typeof error === "string" ? error : error?.message;
        const fieldName = typeof error === "object" ? FIELD_NAME_BY_PAYLOAD[error?.field] : undefined;
        if (fieldName && text) {
          fieldErrors.push({ name: fieldName, errors: [text] });
        } else if (text) {
          generalMessages.push(text);
        }
      });

      if (fieldErrors.length) form.setFields(fieldErrors);
      message.error(
        generalMessages.join(", ") ||
        result?.response?.message ||
        "Failed to save finance settings."
      );
    }
  };

  return (
    <Spin spinning={loading}>
      <FinanceStyle>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" style={{ marginBottom: "8px" }}>
          <label
            style={{ color: "#636980", cursor: "pointer" }}
            onClick={() => history.push("/finance-settings")}
          >
            Finance Settings
          </label>
          <Typography color="text.primary">{entityName || "-"}</Typography>
        </Breadcrumbs>
        <div className="d-flex justify-content-between">
          <div className="finance_header_content" />
          <div className="finance_header_action">
            {canEdit && hasLoaded && !loadError && !isEditing && (
              <Button type="primary" onClick={handleEdit} disabled={loading}>
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Editing upserts: saving creates the row for this entity if none
            exists yet, otherwise updates the existing one. */}
        {hasLoaded && loadError ? (
          <div className="styled-card" style={{ marginTop: "10px" }}>
            <p className="mb-0" style={{ color: "#FF5353" }}>
              Unable to load finance settings: {loadError}
            </p>
          </div>
        ) : (
          <div className="styled-card" style={{ marginTop: "10px" }}>
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              disabled={!isEditing}
              validateTrigger={["onChange", "onBlur"]}
            >
              <div className="finance_section">
                <div className="finance_section_title">Company Info</div>
                <Row gutter={16}>
                  {renderFields(COMPANY_FIELDS)}
                </Row>
              </div>

              <div className="finance_section">
                <div className="finance_section_title">Bank Info</div>
                <Row gutter={16}>{renderFields(BANK_FIELDS)}</Row>
              </div>

              {isEditing && (
                <div className="finance_footer_action">
                  <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={() => form.submit()}>
                    {saving && <Spin indicator={<LoadingOutlined spin />} />}Update
                  </Button>
                </div>
              )}
            </Form>
          </div>
        )}
      </FinanceStyle>
    </Spin>
  );
}

export default FinanceSettings;